import { useState } from "react";

import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { getApiErrorMessage } from "@/api/client";
import { FormButton } from "@/components/form/FormButton";
import { FormFieldError } from "@/components/form/FormField";
import { Icon } from "@/components/icons/Icon";
import { useStore } from "@/store";
import { twMerge } from "@/utils/twMerge";

import type { DocumentPermission } from "@/api";
import type { Scope } from "@/models/document/DocumentPermissionsModel";

export const AcceptDeclineButtons = observer(function AcceptDeclineButtons({
  request,
  permission,
}: {
  request: DocumentPermission;
  permission?: DocumentPermission;
}) {
  const t = useTranslations();
  const { doc } = useStore();
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [error, setError] = useState<string | null>(null);

  if (!doc || !request.team) {
    return null;
  }

  const handleDecline = async () => {
    setStatus("loading");
    try {
      await doc.permissions.deletePermission(request.uid);
      setStatus("success");
    } catch (err) {
      setError(getApiErrorMessage(err));
      setStatus("error");
    }
  };
  const handleAccept = async () => {
    setStatus("loading");
    try {
      await doc.permissions.createPermission(
        { permalink: request.team?.permalink ?? "" },
        request.scope as Scope,
        request.uid,
        permission,
      );
      setStatus("success");
    } catch (err) {
      setError(getApiErrorMessage(err));
      setStatus("error");
    }
  };

  let requestText = "";
  if (request.scope === "write") {
    requestText = t("share.requestEditingAccess");
  } else if (request.scope === "comment") {
    requestText = t("share.requestCommentAccess");
  } else if (request.scope === "read") {
    requestText = t("share.requestViewAccess");
  }

  let allowText = "";
  if (request.scope === "write") {
    allowText = t("share.allowEditing");
  } else if (request.scope === "comment") {
    allowText = t("share.allowComment");
  } else if (request.scope === "read") {
    allowText = t("share.allowView");
  }

  if (status === "success") {
    return null;
  }

  return (
    <div className="flex flex-row mt-1.5 ml-10 mb-2.5 pl-1.5 sm:ml-14">
      {error && <FormFieldError message={error} />}
      {status === "loading" && <Icon name="Spinner" className="m-auto animate-spin inline-block" />}
      {status === "idle" && (
        <div className="flex flex-1 flex-col -mt-1.5">
          {requestText && <div className="mb-1.5 text-size-16 tracking-tight">{requestText}</div>}
          <div className="flex flex-row">
            <FormButton
              type="button"
              className={twMerge("flex-1 text-xs h-8.5 mr-2 tracking-tight rounded-lg")}
              onClick={handleDecline}
            >
              {t("share.decline")}
            </FormButton>
            <FormButton
              type="button"
              className={twMerge("flex-1 text-xs h-8.5 tracking-tight rounded-8")}
              variant="accent"
              onClick={handleAccept}
            >
              {allowText}
            </FormButton>
          </div>
        </div>
      )}
    </div>
  );
});
